/**
 * <gj-facets> (handoff v2 §C): collection filters, sorting and grid columns without page reloads.
 * - Changes in the drawer form (and chip / clear links marked [data-gj-facet-link]) fetch the same URL through
 *   the Section Rendering API and swap every [data-gj-swap] element by id; the URL is updated so the filtered page
 *   can be shared and the back button keeps working.
 * - The drawer form is never replaced (focus and scroll stay): its counts and disabled states are copied over.
 * - Price bands write the filter.v.price.gte / lte hidden inputs.
 * - The column toggle (mobile 1/2, desktop 3/4) is remembered in localStorage.
 */

const COLS_KEY = 'gj:collection-cols';

class GjFacets extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.form = /** @type {HTMLFormElement | null} */ (this.querySelector('form[data-gj-facets-form]'));
    this.form?.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#onPopState);
    this.#syncBands();
    this.#applyCols(this.#readCols());
  }

  disconnectedCallback() {
    this.form?.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener('popstate', this.#onPopState);
  }

  /* ---------- Columns ---------- */

  #readCols() {
    try {
      return JSON.parse(localStorage.getItem(COLS_KEY) ?? '{}');
    } catch {
      return {};
    }
  }

  /** @param {{ mobile?: string, desktop?: string }} cols */
  #applyCols(cols) {
    const grid = this.querySelector('[data-gj-grid]');
    const desktop = window.matchMedia('(min-width: 990px)').matches;
    const value = (desktop ? cols.desktop : cols.mobile) ?? (desktop ? '4' : '2');
    grid?.setAttribute('data-cols', value);
    for (const button of this.querySelectorAll('[data-gj-cols]')) {
      const isDesktop = button.classList.contains('gj-cols--desktop');
      const current = (isDesktop ? cols.desktop : cols.mobile) ?? (isDesktop ? '4' : '2');
      button.setAttribute('aria-pressed', String(button.getAttribute('data-gj-cols') === current));
    }
  }

  /* ---------- Events ---------- */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const cols = event.target.closest('[data-gj-cols]');
    if (cols) {
      const saved = this.#readCols();
      const key = cols.classList.contains('gj-cols--desktop') ? 'desktop' : 'mobile';
      saved[key] = cols.getAttribute('data-gj-cols');
      try {
        localStorage.setItem(COLS_KEY, JSON.stringify(saved));
      } catch {
        /* storage unavailable: the choice lasts for this page */
      }
      this.#applyCols(saved);
      return;
    }

    const band = event.target.closest('.gj-band');
    if (band instanceof HTMLElement) {
      const pressed = band.getAttribute('aria-pressed') === 'true';
      this.#setBand(pressed ? null : band);
      this.#submit();
      return;
    }

    const link = event.target.closest('a[data-gj-facet-link]');
    if (link instanceof HTMLAnchorElement) {
      event.preventDefault();
      this.#render(link.href);
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === 'hidden') return;
    this.#submit();
  };

  #onPopState = () => this.#render(window.location.href, false);

  /* ---------- Price bands ---------- */

  /** @param {HTMLElement | null} band */
  #setBand(band) {
    const gte = this.querySelector('[data-gj-price="gte"]');
    const lte = this.querySelector('[data-gj-price="lte"]');
    if (gte instanceof HTMLInputElement) gte.value = band?.dataset.gte ?? '';
    if (lte instanceof HTMLInputElement) lte.value = band?.dataset.lte ?? '';
    this.#syncBands();
  }

  #syncBands() {
    const gte = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-gj-price="gte"]'));
    const lte = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-gj-price="lte"]'));
    for (const band of this.querySelectorAll('.gj-band')) {
      if (!(band instanceof HTMLElement)) continue;
      const same = (a = '', b = '') => Number(a || 0) === Number(b || 0) && Boolean(a) === Boolean(b);
      band.setAttribute('aria-pressed', String(same(band.dataset.gte, gte?.value) && same(band.dataset.lte, lte?.value)));
    }
  }

  /* ---------- Rendering ---------- */

  #submit() {
    if (!this.form) return;
    const params = new URLSearchParams();
    for (const [name, value] of new FormData(this.form)) {
      if (typeof value === 'string' && value !== '') params.append(name, value);
    }
    this.#render(`${this.form.action}?${params}`);
  }

  /** @param {string} href @param {boolean} [push] */
  async #render(href, push = true) {
    const url = new URL(href, window.location.origin);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');

    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.dataset.sectionId ?? '');

    this.#controller?.abort();
    this.#controller = new AbortController();
    const results = this.querySelector('.gj-results');
    results?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(fetchUrl, { signal: this.#controller.signal });
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      for (const fresh of doc.querySelectorAll('[data-gj-swap][id]')) {
        const current = document.getElementById(fresh.id);
        if (current) current.replaceWith(document.importNode(fresh, true));
      }
      this.#syncForm(doc);
      this.#applyCols(this.#readCols());
      if (push) history.pushState({}, '', url);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // Network failure: fall back to a normal navigation
      window.location.assign(url);
    } finally {
      this.querySelector('.gj-results')?.removeAttribute('aria-busy');
    }
  }

  /** Copy checked / disabled states and counts from the fresh drawer form. @param {Document} doc */
  #syncForm(doc) {
    const fresh = doc.querySelector('form[data-gj-facets-form]');
    if (!fresh || !this.form) return;

    const inputs = this.form.querySelectorAll('input');
    fresh.querySelectorAll('input').forEach((input, index) => {
      const current = inputs[index];
      if (!(current instanceof HTMLInputElement) || current.name !== input.name || current.value !== input.value) {
        if (current instanceof HTMLInputElement && current.type === 'hidden' && current.name === input.name) {
          current.value = input.value;
        }
        return;
      }
      current.checked = input.checked;
      current.disabled = input.disabled;
      current.closest('.gj-check')?.classList.toggle('is-disabled', input.disabled);
    });

    const counts = this.form.querySelectorAll('.gj-check__count');
    fresh.querySelectorAll('.gj-check__count').forEach((count, index) => {
      const current = counts[index];
      if (current) current.textContent = count.textContent;
    });
    this.#syncBands();
  }
}

if (!customElements.get('gj-facets')) customElements.define('gj-facets', GjFacets);
